import { useEffect, useMemo } from 'react';
import { createShallowStore, createStore } from '@frontend/store';
import { useAlert } from '@frontend/design-system';
import {
  useGetSelectedCalendarDateAppointmentInfo,
  useGetSelectedCalendarDateAppointmentList,
} from '../hooks/use-get-selected-calendar-date-appointment-info';
import { ExtendedAppointment } from '../types';

type State = {
  selectedAppointments: ExtendedAppointment[];
  selectedAppointment?: ExtendedAppointment;
};

type Actions = {
  setSelectedAppointments: (appointments: ExtendedAppointment[]) => void;
  setSelectedAppointment: (appointment: ExtendedAppointment, reset?: boolean) => void;
  resetSelectedAppointment: () => void;
  resetSelection: () => void;
};

const initialState: State = {
  selectedAppointments: [],
};

type ScheduleAppointmentInfoPanelStore = State & Actions;

const useStore = createStore<ScheduleAppointmentInfoPanelStore>(
  (set) => ({
    ...initialState,
    setSelectedAppointments: (appointments) => {
      set({
        selectedAppointments: appointments,
        // auto select appointment if only one is selected
        selectedAppointment: appointments.length === 1 ? appointments[0] : undefined,
      });
    },
    setSelectedAppointment: (appointment, reset = false) => {
      if (reset) {
        set({ selectedAppointment: appointment, selectedAppointments: [] });
      } else {
        set({ selectedAppointment: appointment });
      }
    },
    resetSelectedAppointment: () => set({ selectedAppointment: undefined }),
    resetSelection: () => set({ selectedAppointment: undefined, selectedAppointments: [] }),
  }),
  {
    name: 'ScheduleAppointmentInfoPanelStore',
    enabled: true,
    trace: true,
  }
);

export const useScheduleAppointmentInfoPanelShallowStore = createShallowStore(useStore);

export const useSetCalendarAppointmentInfoAsSelected = (eventId: string) => {
  const alerts = useAlert();
  const { setSelectedAppointment, selectedAppointment, resetSelection } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointment',
    'selectedAppointment',
    'resetSelection'
  );
  const appointmentInfo = useGetSelectedCalendarDateAppointmentInfo(eventId);

  return {
    isSelected: selectedAppointment?.id === eventId,
    selectedAppointment,
    setAppointment: (reset?: boolean) => {
      // TODO: temporary show alert until integrated fully
      if (!appointmentInfo) {
        alerts.error('Unable to find appointment details');
        return;
      }
      setSelectedAppointment(appointmentInfo, reset);
    },
    resetSelection,
  };
};

export const useSetCalendarAppointmentListAsSelected = (eventIds: string[]) => {
  const { setSelectedAppointments, selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointments',
    'selectedAppointments'
  );
  const appointments = useGetSelectedCalendarDateAppointmentList(eventIds);

  const selectedAppointmentIds = useMemo(() => selectedAppointments.map((appt) => appt.id), [selectedAppointments]);

  const hasSelectedAppointmentsInList = useMemo(
    () => eventIds.some((eventId) => selectedAppointmentIds.includes(eventId)),
    [eventIds, selectedAppointmentIds]
  );

  const shouldSyncSelection = selectedAppointmentIds.length !== eventIds.length;

  useEffect(() => {
    if (hasSelectedAppointmentsInList && shouldSyncSelection) {
      setSelectedAppointments(appointments);
    }
  }, [appointments, hasSelectedAppointmentsInList, shouldSyncSelection, setSelectedAppointments]);

  return {
    isSelected: hasSelectedAppointmentsInList,
    selectedAppointments,
    setAppointments: () => setSelectedAppointments(appointments),
  };
};
